import type { TFunction } from 'i18next'
import type { PathSession } from '~/pages/scorm/SessionPathView'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Un parcours de démonstration pour la visite du centre d'aide (#105), en forme
 * `mySessions` : il passe par les mêmes helpers que le vrai écran. Il montre un
 * état de chaque sorte — terminé, en cours, fermé par un score, fermé jusqu'à
 * une date — pour que la visite ait quelque chose à désigner partout.
 */
export function demoPath(t: TFunction, now = new Date()): PathSession {
  const module = (id: string, durationMinutes: number, masteryScore: number | null = null) => ({
    id,
    title: t(`helpDemo.path.${id}.title`),
    description: t(`helpDemo.path.${id}.description`),
    status: 'READY',
    durationMinutes,
    masteryScore,
  })

  return {
    id: 'demo-path',
    name: t('helpDemo.path.name'),
    progressionMode: 'FREE',
    // Fiche formation (#319) : remplie, pour que la visite ait de quoi la montrer.
    objectives: [t('helpDemo.path.sheet.objective1'), t('helpDemo.path.sheet.objective2')],
    audience: t('helpDemo.path.sheet.audience'),
    prerequisites: t('helpDemo.path.sheet.prerequisites'),
    contact: t('helpDemo.path.sheet.contact'),
    myEnrolledAt: new Date(now.getTime() - 7 * DAY_MS).toISOString(),
    progress: { totalModules: 4, completedModules: 1, completed: false },
    certificate: { unlocked: false, reason: 'INCOMPLETE', blockingModuleIds: ['essentials', 'cases', 'assessment'] },
    sections: [
      { id: 'demo-basics', title: t('helpDemo.path.basics'), position: 0 },
      { id: 'demo-further', title: t('helpDemo.path.further'), position: 1 },
    ],
    modules: [
      {
        id: 'demo-step-1',
        position: 0,
        sectionId: 'demo-basics',
        unlocked: true,
        lockReason: 'OPEN',
        module: module('welcome', 10, 0.7),
        myTracking: { completionStatus: 'COMPLETED', scoreRaw: 85, scoreMax: 100, totalTimeSeconds: 720 },
      },
      {
        id: 'demo-step-2',
        position: 1,
        sectionId: 'demo-basics',
        unlocked: true,
        lockReason: 'OPEN',
        module: module('essentials', 20, 0.8),
        myTracking: { completionStatus: 'INCOMPLETE', scoreRaw: 60, scoreMax: 100, totalTimeSeconds: 300 },
      },
      {
        id: 'demo-step-3',
        position: 2,
        sectionId: 'demo-further',
        unlocked: false,
        lockReason: 'AWAITING_SCORE',
        prerequisiteModuleId: 'essentials',
        prerequisiteMinScore: 0.8,
        module: module('cases', 25),
      },
      {
        id: 'demo-step-4',
        position: 3,
        sectionId: 'demo-further',
        unlocked: false,
        lockReason: 'SCHEDULED',
        unlockAt: new Date(now.getTime() + 14 * DAY_MS).toISOString(),
        module: module('assessment', 15, 0.8),
      },
    ],
  }
}
